import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
// import { NestedComponentRenderer } from '@zempler/theme-zempler/src/components/componentRenderer/NestedComponentRenderer';
import { Stack } from "../stack/Stack";
import { TwoColumnContext } from "../../context/TwoColumnContext";
import { StyledImage } from "../image/Image";
import { useBackgroundColor } from "../../utils";
import { ThemeColorsKeys } from "../../styles/tokens/colors";

// Interface

export interface TwoColumnProps {
  centered?: boolean;
  border?: boolean;
  columnOneComponents?: React.ReactNode;
  columnTwoComponents?: React.ReactNode;
  columnOneHasImage: boolean;
  columnTwoHasImage: boolean;
  columnBackground?: ThemeColorsKeys;
}

export interface StyledTwoColumnProps {
  centered?: boolean;
  border?: boolean;
}

export interface StyledTwoColumnColumnProps {
  wide?: boolean;
  border?: boolean;
  noBorderedPadding?: boolean;
  columnBackground?: ThemeColorsKeys;
  backgroundWithNoBorder?: boolean;
}

// Styles

export const StyledTwoColumnColumn = styled(Stack)<StyledTwoColumnColumnProps>`
  ${({
    theme: { space, colors, media, maxMedia, borderRadii },
    wide,
    noBorderedPadding,
    border,
    columnBackground,
    backgroundWithNoBorder,
  }) => css`
    @media (min-width: ${media.large}) {
      flex-basis: ${wide ? `calc(50% + ${space.xxLarge})` : "50%"};
      max-width: ${wide ? `calc(50% + ${space.xxLarge})` : "50%"};
      min-height: 420px;
    }

    ${backgroundWithNoBorder &&
    css`
      ${useBackgroundColor(columnBackground)}
      padding: ${space.fluidSmallToXLarge};
      border-radius: ${borderRadii.small};
      border: solid 1px ${colors.darkBlue};
    `};

    ${border &&
    css`
      padding: ${noBorderedPadding ? 0 : space.fluidSmallToXLarge};
      position: relative;
      ${useBackgroundColor(columnBackground)}

      ${noBorderedPadding &&
      css`
        overflow: hidden;

        > * {
          border-radius: 0%;
          border: none;
        }
      `};

      @media (max-width: ${maxMedia.large}) {
        &:first-of-type {
          border-bottom: solid 1px ${colors.darkBlue};
        }
      }

      @media (min-width: ${media.large}) {
        &:first-of-type {
          border-right: solid 1px ${colors.darkBlue};
        }

        ${noBorderedPadding &&
        css`
          overflow: hidden;

          ${StyledImage} {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            border-radius: 0 !important;
            border: none !important;
          }

          .gatsby-image-wrapper {
            height: 100%;
          }
        `};
      }
    `}
  `};
`;

export const StyledTwoColumn = styled.div<StyledTwoColumnProps>`
  ${({
    theme: { space, colors, media, maxMedia, borderRadii },
    centered,
    border,
  }) => css`
    display: flex;
    gap: ${space.medium};

    @media (max-width: ${maxMedia.large}) {
      flex-direction: column;
    }

    @media (min-width: ${media.large}) {
      gap: ${space.xxxLarge};

      ${centered &&
      !border &&
      css`
        align-items: center;
      `};
    }

    // Border

    ${border &&
    css`
      border: solid 1px ${colors.darkBlue};
      border-radius: ${borderRadii.small};
      overflow: hidden;
      gap: 0;
      isolation: isolate;

      @media (min-width: ${media.large}) {
        gap: 0;
      }
    `};
  `};
`;

// JSX

export const TwoColumn: FC<TwoColumnProps> = ({
  centered,
  border,
  columnOneComponents,
  columnTwoComponents,
  columnOneHasImage,
  columnTwoHasImage,
  columnBackground,
}) => {
  const leftColumnIsWide = columnOneHasImage;
  const rightColumnIsWide = columnTwoHasImage;
  const equalColumnWidth =
    (leftColumnIsWide && rightColumnIsWide) ||
    (!leftColumnIsWide && !rightColumnIsWide) ||
    border;

  const leftColumnMediaOnly =
    (border && leftColumnIsWide) || (columnBackground && leftColumnIsWide);
  const rightColumnMediaOnly =
    (border && rightColumnIsWide) || (columnBackground && rightColumnIsWide);

  const rightColumnHasBackgroundAndNoBorder =
    !border && columnOneHasImage && columnBackground;
  const leftColumnHasBackgroundAndNoBorder =
    !border && columnTwoHasImage && columnBackground;

  return (
    <TwoColumnContext.Provider value>
      <StyledTwoColumn centered={centered} border={border}>
        <StyledTwoColumnColumn
          gap="fluidMediumToLarge"
          wide={leftColumnIsWide && !equalColumnWidth}
          noBorderedPadding={leftColumnMediaOnly}
          border={border}
          columnBackground={columnBackground ? columnBackground : "white"}
          backgroundWithNoBorder={leftColumnHasBackgroundAndNoBorder}
        >
          {columnOneComponents}
        </StyledTwoColumnColumn>
        <StyledTwoColumnColumn
          gap="fluidMediumToLarge"
          wide={rightColumnIsWide && !equalColumnWidth}
          noBorderedPadding={rightColumnMediaOnly}
          border={border}
          columnBackground={columnBackground ? columnBackground : "white"}
          backgroundWithNoBorder={rightColumnHasBackgroundAndNoBorder}
        >
          {columnTwoComponents}
        </StyledTwoColumnColumn>
      </StyledTwoColumn>
    </TwoColumnContext.Provider>
  );
};

export default TwoColumn;
